import {
  convertDbSizeToBytes,
  addSlug,
  slimUserSchema,
  projectStatusesSchema,
  projectStatusTransform,
  thumbnailRequiredSchema,
  singleFileSchema,
} from 'shared';
import {z} from 'zod';

import {MIN_IMAGES_FOR_3D, MIN_IMAGES_FOR_SONAR} from '@/config';

import {validation} from '../orthoRequiresThree';

import {fileListAndCountSchema} from './fileListAndCount';

const baseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  statuses: projectStatusesSchema,
  thumbnail: z.string(),
  created: z.coerce.date(),
  modified: z.coerce.date(),
  isCompleted: z.boolean(),
  pixels: z
    .number() // MPx
    .nullable()
    .transform((n) => (!n ? null : n / 1000)), // convert to GPx
  size: z
    .number() // MB
    .nonnegative()
    .transform((n) => convertDbSizeToBytes(n, 'collection')),
  asset: z
    .object({
      id: z.string(),
      name: z.string(),
      thumbnail: z.string(),
    })
    .transform(addSlug),
});

export const projectSchema = baseSchema
  .extend({
    createdBy: slimUserSchema.nullable(),
    checklist: z.string().min(1).nullable(),
    hasCaptureData: z.boolean(),
    hasFinishedLayout: z.boolean(),
    modules: z.object({
      three: z.object({
        rgb: z.object({
          count: z.number().nonnegative(),
        }),
        ir: z.object({
          count: z.number().nonnegative(),
        }),
        isAr: z.boolean(),
      }),
      two: z.object({
        rgb: z.object({
          count: z.number().nonnegative(),
        }),
        ir: z.object({
          count: z.number().nonnegative(),
        }),
        qfs: z.number().nonnegative().nullable(),
      }),
      ortho: z.object({
        gcp: z.boolean(),
        isEnabled: z.boolean(),
      }),
      sonar: z.object({
        data: z.object({
          count: z.number().nonnegative(),
        }),
      }),
      supplementary: z.object({
        data: z.object({
          count: z.number().nonnegative(),
        }),
      }),
    }),
    site: z
      .object({
        id: z.string(),
        name: z.string(),
        thumbnail: z.string(),
      })
      .transform(addSlug),
  })
  .transform(addSlug)
  .transform(projectStatusTransform);

export const projectListSchema = baseSchema
  .extend({imageCount: z.number().nonnegative()})
  .transform(addSlug)
  .transform(projectStatusTransform)
  .array();

const getNewAndOldCount = (input: z.infer<typeof fileListAndCountSchema>) =>
  input.new.length + input.count;
const getFileNames = (fileNames: string[]) =>
  fileNames.map((fileName) => fileName.split('.').slice(0, -1).join('.'));
const checkMatchingFilenames = (
  filesListOne: string[],
  filesListTwo: string[]
) => {
  return filesListOne.every((name) => filesListTwo.includes(name));
};

export const projectPayloadSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2)
      .max(35)
      .regex(/^[a-z0-9\s]+$/i, 'Only letters and numbers are allowed.')
      .transform((name) => name.replace(/\s+/g, '')), // remove spaces
    assetId: z.string().min(1),
    checklistId: z.string().min(1),
    description: z.string().trim().max(999),
    thumbnail: thumbnailRequiredSchema,
    shouldProcessAfter: z.boolean(),
    modules: z
      .object({
        three: z
          .object({
            isEnabled: z.boolean(),
            rgb: fileListAndCountSchema,
            ir: fileListAndCountSchema,
            isAr: z.boolean(),
          }) // require images if tab is enabled
          .refine(
            (values) =>
              !values.isEnabled ||
              getNewAndOldCount(values.rgb) > MIN_IMAGES_FOR_3D ||
              getNewAndOldCount(values.ir) > MIN_IMAGES_FOR_3D,
            {
              message: `You must upload at least ${MIN_IMAGES_FOR_3D} visual or thermal data for a 3D Digital Twin.`,
              path: ['isEnabled'],
            }
          ),
        two: z
          .object({
            isEnabled: z.boolean(),
            rgb: fileListAndCountSchema,
            ir: fileListAndCountSchema,
          })
          // require images if tab is enabled
          .refine(
            (values) =>
              !values.isEnabled ||
              getNewAndOldCount(values.rgb) > 0 ||
              getNewAndOldCount(values.ir) > 0,
            {
              message: `You must upload at least 1 visual or thermal data for a 2D inpsection.`,
              path: ['isEnabled'],
            }
          ),
        ortho: z.object({
          isEnabled: z.boolean(),
          gcp: singleFileSchema,
        }),
        sonar: z
          .object({
            isEnabled: z.boolean(),
            images: fileListAndCountSchema,
            params: fileListAndCountSchema,
          })
          // require images if tab is enabled
          .refine(
            (values) =>
              !values.isEnabled ||
              getNewAndOldCount(values.images) > MIN_IMAGES_FOR_SONAR,
            {
              message: `You must upload at least ${MIN_IMAGES_FOR_SONAR} sonar data with matching .txt parameter files for a sonar inspection.`,
              path: ['isEnabled'],
            }
          )
          // equal length and matching names for sonar bmps and the param txts
          .refine(
            (values) =>
              getNewAndOldCount(values.params) ===
                getNewAndOldCount(values.images) &&
              checkMatchingFilenames(
                getFileNames(values.images.new.map((file) => file.name)),
                getFileNames(values.params.new.map((file) => file.name))
              ),
            {
              message:
                'Each sonar .bmp data should have a matching .txt parameter file. The names should be identical before the file extension.',
              path: ['isEnabled'],
            }
          ),
        supplementary: z
          .object({
            isEnabled: z.boolean(),
            fileMetas: fileListAndCountSchema,
          })
          // require files if tab is enabled
          .refine(
            (values) =>
              !values.isEnabled || getNewAndOldCount(values.fileMetas) > 0,
            {
              message: `You must upload at least one supplementary data.`,
              path: ['isEnabled'],
            }
          ),
      })
      .refine(
        (modules) => {
          return validation.orthoRequiresThree.validate(
            modules.ortho.isEnabled,
            modules.three.isEnabled
          );
        },
        {
          message: `Orthomosaics require a 3D Digital Twin.`,
          path: ['ortho', 'isEnabled'],
        }
      ),
  })
  .refine(
    (values) =>
      values.modules.two.isEnabled ||
      values.modules.three.isEnabled ||
      values.modules.sonar.isEnabled ||
      values.modules.supplementary.isEnabled ||
      values.modules.ortho.isEnabled,
    {
      message: 'You must select at least one data type.',
      path: ['shouldProcessAfter'],
    }
  );
